import React from 'react';
import { observer } from 'mobx-react-lite';
import { Window, WindowHeader } from '../overlays';
import { Button } from '../input';
import { DeceptioScoreLevels, getDeceptioScoreLevel } from '../playerTranscripts/playerTranscriptsState';
import { DeceptioWindowState } from './deceptioWindowState';
import './deceptioWindow.scss';

type Props = {
  model: DeceptioWindowState;
  visible?: boolean;
};

export const DeceptioAnalysis = observer(({ model }: { model: DeceptioWindowState }) => {
  const { analysis } = model;
  if (!analysis)
    return null;

  const level = getDeceptioScoreLevel(analysis.score);
  const band = DeceptioScoreLevels.find(item => item.level === level);
  const segments = model.statementSegments;
  const selected = model.selectedResult;

  return (
    <div className="deceptio-analysis">
      <section className={`deceptio-analysis-score deceptio-${level ?? 'unbanded'}`}>
        <strong>
          <span className="deceptio-analysis-score-label">Truth Score</span>
          <span className="deceptio-analysis-score-value">{analysis.score}%</span>
        </strong>
        {band && <span className="deceptio-analysis-band">{band.label}</span>}
      </section>

      <div className="deceptio-analysis-columns">
        <section className="deceptio-analysis-panel deceptio-analysis-statement">
          <h4>Analysed statement</h4>
          {segments.length > 0 ?
            <p>
              {segments.map((segment, index) => (segment.highlighted ?
                <mark key={index}>{segment.text}</mark> :
                <React.Fragment key={index}>{segment.text}</React.Fragment>))}
            </p> :
            <p className="deceptio-analysis-empty">Statement text unavailable.</p>}
        </section>

        <section className="deceptio-analysis-panel deceptio-analysis-keywords">
          <h4>Keywords found</h4>
          {analysis.results.length > 0 ?
            <ul>
              {analysis.results.map((result, index) => (
                <li key={`${result.name}-${index}`}>
                  <button
                    type="button"
                    className={index === model.selectedResultIndex ? 'selected' : undefined}
                    aria-pressed={index === model.selectedResultIndex}
                    onClick={() => model.selectResult(index)}>
                    {result.name} ({result.ranges.length})
                  </button>
                </li>
              ))}
            </ul> :
            <p className="deceptio-analysis-empty">No indicators found.</p>}
        </section>
      </div>

      <section className="deceptio-analysis-panel">
        <h4>Keyword explanation</h4>
        {selected ?
          <p className="deceptio-analysis-description">{selected.description}</p> :
          <p className="deceptio-analysis-empty">Select a keyword to see why it was flagged.</p>}
      </section>

      <section className="deceptio-analysis-panel">
        <h4>Follow up questions</h4>
        {/* The optional chain has to cover the array too: the schema says non-null, but a client
            running against a backend deployed before the field existed gets undefined here, and
            reading .length off it throws mid-render. */}
        {selected?.followupQuestions?.length ?
          <ol>
            {selected.followupQuestions.map((question, index) => <li key={index}>{question}</li>)}
          </ol> :
          <p className="deceptio-analysis-empty">
            {selected ? 'No follow up questions for this keyword.' : 'Select a keyword to see suggested questions.'}
          </p>}
      </section>
    </div>
  );
});

export const DeceptioWindow = observer((props: Props) => {
  const { model } = props;

  return (
    <Window
      className="deceptio-window"
      model={model.window}
      visible={props.visible}
    >
      <WindowHeader model={model.window} title="Deceptio AI" />

      <hr />

      <div className="window-content">
        {model.isLoading ? (
          <p>Loading Deceptio analysis...</p>
        ) : model.error ? (
          <p>{model.error}</p>
        ) : model.analysis ? (
          <DeceptioAnalysis model={model} />
        ) : model.iFrameUrl ? (
          <iframe
            title="Deceptio AI"
            key={model.iFrameUrl}
            className="deceptio-iframe"
            src={model.iFrameUrl}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            allow="accelerometer; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        ) : (
          <p>No URL provided.</p>
        )}
      </div>

      <hr />

      <footer className="window-footer">
        <div className="btn-group right">
          <Button
            className="primary btn-outline medium"
            label="Close"
            onClick={() => model.close()}
          />
        </div>
      </footer>
    </Window>
  );
});
